import React, { useState } from 'react';
import SubPage from '../components/site/SubPage';
import { PageSection } from '../components/site/SubPageParts';

const IMG = '/images/site/company';

// Bios are transcribed from the supplied resumes. `bio` is a list of blocks so
// a profile can carry a heading and a bullet list, not just paragraphs.
const TEAM = [
  {
    name: 'Cary Westbrook',
    role: 'Founder & CEO',
    bio: [
      {
        type: 'p',
        text: 'Entrepreneurial thinker that designs frontier approaches operating at the intersection of visionary thinking, respect for personal privacy, social needs, and radical technological design.',
      },
      { type: 'h', text: 'Snapshot' },
      {
        type: 'ul',
        items: [
          '2010 VenturePrise SaaS Winner for high-growth startups / Eyessoftware.',
          'Directed R & D of AI solutions targeting financial crimes for Bank of America.',
          'Collaborated with the FBI on online teen exploitation.',
          'Keynote speaker at technology symposiums.',
        ],
      },
      { type: 'h', text: 'Personal note' },
      {
        type: 'p',
        text: 'To be completely open, this is not simply a technology exercise for me or something that sounds interesting. My aging family member lost over $250k in various online scams. I’ve talked to the scammers, seen the text messages, heard the lies, seen the grooming playbook up close and understand the family hardship that scammers inflict.',
      },
      {
        type: 'p',
        text: 'A retired USAF veteran, Cary holds a B.A. in Computer Information Systems from Our Lady of the Lake University.',
      },
    ],
  },
  {
    name: 'Scott Jordan',
    role: 'CTO',
    bio: [
      {
        type: 'p',
        text: 'Scott began his IT career in the U.S. Military and brings more than 15 years of experience in IT infrastructure and cybersecurity. Currently serving as Director of Infrastructure in the hospitality industry, he combines deep technical expertise with a strategic, customer-focused approach to technology.',
      },
      {
        type: 'p',
        text: 'Throughout his career, he has led initiatives that strengthen security, optimize operations, and support scalable growth. His ability to align technology with business objectives positions him to deliver innovative, reliable, and secure solutions that drive long-term success.',
      },
    ],
  },
  {
    name: 'Austin Profenius',
    role: 'Technical Product Lead',
    bio: [
      {
        type: 'p',
        text: 'Austin leads product engineering across No / SCAM, from the public site and subscriber dashboard through to the desktop application and the services behind them. His work spans front-end architecture, authentication and payments, and the data pipelines that turn agency fraud reporting into something a person can act on.',
      },
      {
        type: 'p',
        text: 'He pairs hands-on engineering with a product owner’s eye for what actually reaches the user, favouring evidence over assumption and verifying figures against primary sources before they ship. That discipline is what keeps the product honest about a subject where exaggeration is the norm.',
      },
    ],
  },
];

/**
 * Leadership: the banner and the three team profiles, per slide 67 of the 9-10
 * deck. The company film, Purpose Driven Design, About Us and Investors
 * Thoughts blocks that used to sit above these now live on the Investors page —
 * both nav labels previously pointed at this one route.
 */
const Leadership = () => {
  // A Set rather than a single id: more than one profile can stay open.
  const [openBios, setOpenBios] = useState(() => new Set());

  const toggleBio = (name) =>
    setOpenBios((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  return (
    <SubPage title="Leadership">
      <PageSection className="pt-6">
        {/* The mockup opens on the banner; the page still needs a heading for
            assistive tech and search. */}
        <h1 className="sr-only">Leadership</h1>
        <img
          src={`${IMG}/leadership-hero.jpeg`}
          alt="A wall of binary digits receding into the distance"
          className="h-[150px] w-full object-cover small:aspect-[112/25] small:h-auto"
        />
      </PageSection>

      {/* Team */}
      <PageSection className="pt-10">
        <ul className="space-y-4">
          {TEAM.map((person) => {
            const id = person.name.toLowerCase().replace(/\s+/g, '-');
            const isOpen = openBios.has(person.name);
            return (
              <li key={person.name}>
                <button
                  type="button"
                  onClick={() => toggleBio(person.name)}
                  aria-expanded={isOpen}
                  aria-controls={`bio-${id}`}
                  className={`flex w-full items-center justify-between border border-black/25 px-6 py-4 text-left transition-colors hover:bg-black/[.03] md:px-8 md:py-5 ${
                    isOpen ? 'rounded-t-[10px]' : 'rounded-[10px]'
                  }`}
                >
                  <span className="text-[17px] text-black md:text-[21px]">
                    {person.name} / {person.role}
                  </span>
                  {/* The play triangle rotates to point down when open, so the
                      same mark reads as a disclosure arrow. */}
                  <svg
                    viewBox="0 0 24 24"
                    className={`h-7 w-7 flex-shrink-0 fill-[#8A8A8A] transition-transform md:h-9 md:w-9 ${
                      isOpen ? 'rotate-90' : ''
                    }`}
                    aria-hidden="true"
                  >
                    <path d="M6 3.5v17l15-8.5z" />
                  </svg>
                </button>

                {isOpen && (
                  <div
                    id={`bio-${id}`}
                    className="rounded-b-[10px] border border-t-0 border-black/25 px-6 pb-6 pt-2 md:px-8 md:pb-8"
                  >
                    {person.bio.map((block, i) => {
                      if (block.type === 'h') {
                        return (
                          <h3
                            key={i}
                            className="mt-5 text-[16px] font-bold text-black first:mt-0 md:text-[18px]"
                          >
                            {block.text}
                          </h3>
                        );
                      }
                      if (block.type === 'ul') {
                        return (
                          <ul key={i} className="mt-3 space-y-2">
                            {block.items.map((item) => (
                              <li
                                key={item}
                                className="flex gap-3 text-[15px] leading-relaxed text-black md:text-[17px]"
                              >
                                <span aria-hidden="true">&middot;</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      return (
                        <p
                          key={i}
                          className="mt-3 text-[15px] leading-relaxed text-black first:mt-0 md:text-[17px]"
                        >
                          {block.text}
                        </p>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </PageSection>
    </SubPage>
  );
};

export default Leadership;
